<script>
  import { Button, Heading, Modal } from 'flowbite-svelte';
  import Accordion from './Accordion.svelte';
  import AccordionItem from './AccordionItem.svelte';
  import { _ } from 'svelte-i18n';

  export let isShowingHelpModal;

  const listStyle = 'flex font-medium text-neutral-800 py-2 items-center';
  const bulletStyle =
    'inline-block py-2 px-4 mr-3 font-bold text-white rounded-full bg-neutral-800';
</script>

<Modal
  bind:open={$isShowingHelpModal}
  size="lg"
  placement="center"
  class="max-h-screen !rounded-3xl"
>
  <div class="grid gap-x-10 lg:grid-cols-2">
    <div class="px-10 py-12">
      <Heading tag="h1" class="text-xl">
        {$_('_shared.help.instructions.header', {
          default: 'Instructions',
        })}
      </Heading>
      <p class="!mt-1">
        {$_('_shared.help.instructions.subheader', {
          default: 'How do I create a shortlink?',
        })}
      </p>
      <ul class="!mt-6">
        <li class={listStyle}>
          <span class={bulletStyle}>1</span>
          {$_('_shared.help.instructions.step1', {
            default: 'Find the URL you want to shorten',
          })}
        </li>
        <li class={listStyle}>
          <span class={bulletStyle}>2</span>
          <div class="grid min-w-0">
            <div>
              {$_('_shared.help.instructions.step2', {
                default:
                  'Paste or type the entire URL into the upper entry field',
              })}
            </div>
            <div class="max-w-[200px] p-1 italic">
              <p class="text-xs font-medium leading-relaxed text-gray-500">
                {$_('_shared.help.instructions.step2Note', {
                  default:
                    'It must be an Amazon created link with your TrackingID included',
                })}
              </p>
            </div>
          </div>
        </li>
        <li class={listStyle}>
          <span class={bulletStyle}>3</span>
          {$_('_shared.help.instructions.step3', {
            default: 'Give your shortlink a name using the lower entry field',
          })}
        </li>
        <li class={listStyle}>
          <span class={bulletStyle}>4</span>
          <span class="inline-block">
            {$_('_shared.help.instructions.step4', {
              default:
                'Click “Create Shortlink” to create a unique shortlink that will populate in the table at the bottom of the page',
            })}
          </span>
        </li>
      </ul>
      <div class="pt-10">
        <Button
          on:click={() => ($isShowingHelpModal = false)}
          pill={true}
          size="xl"
          class="px-8 py-5"
        >
          {$_('_shared.help.cta.instructions.confirm', {
            default: "Got it, let's create!",
          })}
        </Button>
      </div>
    </div>
    <div class="!rounded-br-3xl !rounded-tr-3xl bg-gray-100">
      <div class="overflow-auto px-10 py-12">
        <Heading tag="h1" class="mb-4 text-xl">
          {$_('_shared.help.faqs.header', {
            default: 'FAQs',
          })}
        </Heading>
        <Accordion>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q1', {
                default: 'Do shortlinks expire?',
              })}
            </span>
            {$_('_shared.help.faqs.a1', {
              default:
                'No, shortlinks will not expire and can be freely used for all types of posts.',
            })}
          </AccordionItem>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q2', {
                default:
                  'Can I create multiple shortlinks using the same original URL?',
              })}
            </span>
            {$_('_shared.help.faqs.a2', {
              default:
                'Yes! You can create as many shortlinks as you like even if they use the same original URL provided you give them a different name.',
            })}
          </AccordionItem>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q3', {
                default:
                  'Can I input an Amazon shortlink (ie amzn.to) into this tool?',
              })}
            </span>
            {$_('_shared.help.faqs.a3', {
              default:
                'Yes! You can use any Amazon link so long as you created the link properly using Amazon linking tools.',
            })}
          </AccordionItem>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q4', {
                default:
                  'Should I create a different shortlink for each traffic source?',
              })}
            </span>
            {$_('_shared.help.faqs.a4', {
              default:
                'No need! You can use the same shortlink across multiple traffic sources and we will capture which platform the clicks came from.',
            })}
          </AccordionItem>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q5', {
                default: 'How can I validate my new link works?',
              })}
            </span>
            {$_('_shared.help.faqs.a5', {
              default:
                'If you wish to validate functionality, we recommend testing links directly within draft social posts or articles. Please note that links will not deeplink into the Amazon app if pasted directly into a browser address bar.',
            })}
          </AccordionItem>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q6', {
                default:
                  'Can I change the name of a shortlink once it has been created?',
              })}
            </span>
            {$_('_shared.help.faqs.a6', {
              default:
                'Not at this time. However, you can create another shortlink using the same link provided you give it a different name.',
            })}
          </AccordionItem>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q7', {
                default:
                  'The tool won’t let me create a shortlink, what’s going on?',
              })}
            </span>
            {$_('_shared.help.faqs.a7', {
              default:
                'See the error message below the URL input field to find out why you are unable to create a shortlink.',
            })}
            <ul class="mt-2 list-disc px-4">
              <li class="mb-2">
                {$_('_shared.help.faqs.a7Note', {
                  default:
                    'Invalid domain: Please use a valid domain like amazon.com or amzn.to',
                })}
              </li>
            </ul>
          </AccordionItem>
          <AccordionItem>
            <span slot="header">
              {$_('_shared.help.faqs.q8', {
                default: 'Where can I use these links?',
              })}
            </span>
            {$_('_shared.help.faqs.a8p1', {
              default:
                'You can use these links wherever you would otherwise use Amazon links on the open web, like content articles, blog posts, or social posts.',
            })}
            <br /><br />
            {$_('_shared.help.faqs.a8p2', {
              default:
                'For social traffic, Button for Creators links will show the greatest optimization within the following platforms: Instagram, Facebook, YouTube, Pinterest, WhatsApp, Messenger, LinkedIn, and TikTok.',
            })}
          </AccordionItem>
        </Accordion>
        <div
          class="mt-2 flex w-auto justify-center text-blue-500 hover:text-blue-400"
        >
          <a href="/help" target="_blank">
            {$_('_shared.help.cta.faqs.viewAll', {
              default: 'View all',
            })}
          </a>
        </div>
      </div>
    </div>
  </div>

  <style lang="postcss">
    #modal {
      padding: 0;
    }
  </style>
</Modal>
